import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PLANS, money, SITE } from "../data/site";

export const Calculator = ({ defaultPlan = "essentials", defaultCount = 10 }) => {
  const [count, setCount] = useState(defaultCount);
  const [planKey, setPlanKey] = useState(defaultPlan);
  const p = PLANS[planKey];
  const ours = p.base + p.per * count;
  const theirs = p.cbase + p.cper * count;

  return (
    <aside
      className="relative bg-white rounded-2xl border border-slate-200 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] p-6 sm:p-8 w-full max-w-md"
      aria-labelledby="calc-title"
      data-testid="payroll-calculator"
    >
      <p id="calc-title" className="font-display text-lg font-extrabold text-slate-900">Your monthly payroll bill</p>
      <p className="text-sm text-slate-500 mt-1">Adjust your headcount to see what you would pay.</p>

      <div className="mt-6">
        <label htmlFor="calc-range" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
          People paid each month
        </label>
        <div className="flex items-center gap-4 mt-2">
          <input
            id="calc-range"
            data-testid="calculator-range-input"
            type="range" min="1" max="100" value={Math.min(count, 100)}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-full accent-emerald-600"
          />
          <input
            data-testid="calculator-number-input"
            type="number" min="1" max="500" value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
            className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-center font-mono-num text-sm font-bold"
            aria-label="Exact number of people"
          />
        </div>
      </div>

      <fieldset className="mt-5">
        <legend className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Plan</legend>
        <div className="grid grid-cols-3 gap-2">
          {Object.values(PLANS).map((pl) => (
            <label
              key={pl.key}
              data-testid={`calculator-plan-${pl.key}`}
              className={`cursor-pointer rounded-lg border px-2 py-2 text-center text-xs font-bold transition-colors ${
                planKey === pl.key ? "border-emerald-600 bg-emerald-50 text-emerald-800" : "border-slate-200 text-slate-600 hover:border-slate-400"
              }`}
            >
              <input type="radio" name="calc-plan" value={pl.key} checked={planKey === pl.key}
                onChange={() => setPlanKey(pl.key)} className="sr-only" />
              {pl.name}
            </label>
          ))}
        </div>
      </fieldset>

      <dl className="mt-6 space-y-2.5 text-sm" aria-live="polite">
        <div className="flex justify-between"><dt className="text-slate-500">Base fee</dt><dd className="font-mono-num font-bold" data-testid="calculator-base-fee">{money(p.base)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{count} people &times; {money(p.per)}</dt><dd className="font-mono-num font-bold">{money(p.per * count)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Multi-state payroll</dt><dd className="font-bold text-emerald-700">Included</dd></div>
        <div className="flex justify-between border-t border-slate-200 pt-2.5">
          <dt className="font-bold text-slate-900">ABSLpay per month</dt>
          <dd className="font-mono-num text-lg font-bold text-slate-900" data-testid="calculator-total">{money(ours)}</dd>
        </div>
        <div className="flex justify-between text-slate-400">
          <dt>Same team on {p.cname}</dt>
          <dd className="font-mono-num line-through" data-testid="calculator-competitor-total">{money(theirs)}</dd>
        </div>
      </dl>

      <div className="mt-5 border-t border-dashed border-slate-300 pt-5">
        <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
          <span className="text-sm font-semibold text-emerald-900">You keep each year</span>
          <strong className="font-mono-num text-xl font-bold text-emerald-700" data-testid="calculator-savings">
            {money(Math.max(0, (theirs - ours) * 12))}
          </strong>
        </div>
        <Link
          to={`/contact?plan=${planKey}&headcount=${count}`}
          data-testid="calculator-get-quote-btn"
          className="group mt-4 flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white text-sm font-bold px-5 py-3 hover:bg-emerald-700 transition-colors"
        >
          Lock in {money(ours)}/mo — get my quote
          <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
          Comparison uses Gusto's published plan prices, checked {SITE.competitorChecked}. Payroll taxes you owe are
          separate on every provider.
        </p>
      </div>
    </aside>
  );
};
